import React, { FC, memo, ReactNode, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { createPortal } from 'react-dom';

import styles from './modalStyles.scss';

const Modal: FC<{
  children: ReactNode;
  closeOnOverlayClick?: boolean;
  id?: string;
  isOpen: boolean;
  onClose: () => void;
  titleIcon?: string;
  title?: ReactNode | string;
  renderOpenModalButton?: () => ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
}> = memo(
  ({
    children,
    closeOnOverlayClick = true,
    id,
    isOpen,
    onClose,
    renderOpenModalButton,
    size = 'md',
    title
  }) => {
    const overlayRef = useRef<HTMLDivElement>(null);
    const prevFocused = useRef<HTMLElement | null>(null);

    const handleOverlayClick = (e: React.MouseEvent): void => {
      if (e.target === overlayRef.current && closeOnOverlayClick) {
        onClose();
      }
    };

    // lock scroll & restore focus
    useEffect(() => {
      if (!isOpen) return;

      prevFocused.current = document.activeElement as HTMLElement;
      document.body.style.overflow = 'hidden';
      overlayRef.current?.querySelector<HTMLElement>('[role="dialog"]')?.focus();

      return (): void => {
        document.body.style.overflow = '';
        prevFocused.current?.focus();
      };
    }, [isOpen]);

    // ESC to close
    useEffect(() => {
      if (!isOpen) return;

      const onKey = (e: KeyboardEvent): void => {
        e.key === 'Escape' && onClose();
      };

      window.addEventListener('keydown', onKey);

      return (): void => {
        void window.removeEventListener('keydown', onKey);
      };
    }, [isOpen, onClose]);

    return (
      <>
        {renderOpenModalButton ? renderOpenModalButton() : null}
        {isOpen
          ? createPortal(
              <div
                className={styles.modalOverlay}
                id={`modal${id ? `_${id}` : ''}`}
                onClick={handleOverlayClick}
                ref={overlayRef}
                role="presentation"
              >
                <div
                  aria-labelledby={title ? 'modal-title' : undefined}
                  aria-modal="true"
                  className={classNames(
                    styles.modalContainer,
                    styles[`modal${size.charAt(0).toUpperCase() + size.slice(1)}`]
                  )}
                  role="dialog"
                  tabIndex={-1}
                >
                  {title ? (
                    <header className={styles.modalHeader}>
                      <span className={styles.modalTitle} id="modal-title" role="heading">
                        {title}
                      </span>
                      <button
                        aria-label="Close"
                        className={styles.modalClose}
                        onClick={onClose}
                        type="button"
                      >
                        ×
                      </button>
                    </header>
                  ) : null}
                  <div className={styles.modalBody}>{children}</div>
                </div>
              </div>,
              document.body
            )
          : null}
      </>
    );
  }
);

export default Modal;
